import React from 'react';
import PropTypes from 'prop-types';
import { Box, Dialog, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { useTranslation } from 'react-i18next';
import { downloadFile } from 'common/utils/general';

/**
 * Visor de PDF embebido (monocromo OTRIX). Renderiza el PDF inline con `<object>` (+ fallback
 * `<iframe>`, mismo patrón que MealPlan) y ofrece "Abrir en pestaña nueva" y "Descargar".
 * Reutilizable: factura del entrenador (admin) y documentos bancarios del perfil.
 */
function PdfViewModal({ open, src = null, title = null, filename = null, onClose }) {
  const { t } = useTranslation(['labels', 'common']);
  if (!src) return null;

  const handleOpenNewTab = () => window.open(src, '_blank', 'noopener,noreferrer');
  const handleDownload = () => downloadFile(src, filename || `${title || 'documento'}.pdf`);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          backgroundColor: 'surface.content',
          backgroundImage: 'none',
          border: '1px solid',
          borderColor: 'hairline.main'
        }
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: { xs: 2, sm: 3 }, py: 1.5, borderBottom: '1px solid', borderColor: 'hairline.main' }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary', pr: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {title || filename || 'PDF'}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Tooltip title={t('openInNewTab', { ns: 'labels', defaultValue: 'Abrir en pestaña nueva' })}>
            <IconButton onClick={handleOpenNewTab} size="small" sx={{ color: 'gold.main', '&:hover': { backgroundColor: 'gold.soft' } }}>
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('download', { ns: 'labels', defaultValue: 'Descargar' })}>
            <IconButton onClick={handleDownload} size="small" sx={{ color: 'gold.main', '&:hover': { backgroundColor: 'gold.soft' } }}>
              <FileDownloadOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('close', { ns: 'common', defaultValue: 'Cerrar' })}>
            <IconButton onClick={onClose} size="small" aria-label="cerrar" sx={{ color: 'text.secondary', '&:hover': { color: 'text.primary', backgroundColor: 'rgba(255,255,255,0.08)' } }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
        <Box
          component="object"
          data={src}
          type="application/pdf"
          sx={{
            width: '100%',
            height: { xs: '68vh', md: '78vh' },
            border: '1px solid',
            borderColor: 'hairline.main',
            borderRadius: '12px',
            backgroundColor: 'surface.content'
          }}
        >
          <Box
            component="iframe"
            title={title || 'PDF'}
            src={src}
            sx={{ width: '100%', height: '100%', border: 0, borderRadius: '12px' }}
          />
        </Box>
      </Box>
    </Dialog>
  );
}

PdfViewModal.propTypes = {
  open: PropTypes.bool.isRequired,
  src: PropTypes.string,
  title: PropTypes.string,
  filename: PropTypes.string,
  onClose: PropTypes.func.isRequired
};

export default PdfViewModal;
